import { AppShell, Burger, Button, Group, Image, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ArrowLeft, ExternalLink, LogOut, MoreVertical } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useStore from "../../utils/store";
import Sidebar from "./Sidebar";

const Layouts = ({ children }) => {
  const logout = useStore((state) => state.logout);
  const [isMobile, setIsMobile] = useState(false);
  const [opened, { toggle, close }] = useDisclosure(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  return (
    <AppShell
      padding={{ base: "md", sm: "lg" }}
      header={{ height: 56 }}
      navbar={{
        width: 272,
        breakpoint: "md",
        collapsed: { mobile: !opened },
      }}
      styles={{
        main: { backgroundColor: "#000" },
        header: {
          backgroundColor: "#0a0a0a",
          borderBottom: "1px solid #27272a",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        },
        navbar: {
          backgroundColor: "#0a0a0a",
          borderRight: "1px solid #27272a",
        },
      }}
    >
      <AppShell.Header className="px-3 sm:px-4 md:px-5">
        <div className="flex flex-row justify-between items-center h-full min-w-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {isMobile && (
              <Burger
                opened={opened}
                onClick={toggle}
                size="sm"
                color="white"
                className="shrink-0 [&>.mantine-Burger-bread]:bg-zinc-300"
              />
            )}
            {location.pathname !== "/" && (
              <button
                className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors shrink-0"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <ArrowLeft size={18} color="white" />
              </button>
            )}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <Image src="/logo.svg" w={36} h={36} fit="contain" alt="Logo" className="shrink-0 hidden sm:block" />
              <div className="min-w-0">
                <span className="text-white font-semibold text-sm sm:text-base truncate block">
                  T-Burudani Plus
                </span>
                <span className="text-zinc-500 text-xs hidden sm:block">Admin</span>
              </div>
            </div>
          </div>
          <div className="flex flex-row gap-1 sm:gap-2 items-center shrink-0">
            {isMobile ? (
              <Menu shadow="lg" width={220} position="bottom-end" withArrow>
                <Menu.Target>
                  <button className="size-10 flex items-center justify-center rounded-xl bg-zinc-800/80 border border-zinc-700/50 hover:bg-zinc-700/80 transition-all duration-200">
                    <MoreVertical size={20} color="white" />
                  </button>
                </Menu.Target>
                <Menu.Dropdown className="border-zinc-800 bg-zinc-900/95 backdrop-blur-xl">
                  <Menu.Item component="a" href="" target="_blank" rel="noreferrer" className="hover:bg-zinc-800">
                    <div className="flex items-center justify-between">
                      <span>Analytics</span>
                      <ExternalLink size={14} className="opacity-60" />
                    </div>
                  </Menu.Item>
                  <Menu.Divider className="border-zinc-700" />
                  <Menu.Item
                    className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    leftSection={<LogOut size={14} />}
                    onClick={() => {
                      logout();
                      navigate("/login", { replace: true });
                    }}
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Group gap="xs">
                <a href="" target="_blank" rel="noreferrer">
                  <Button variant="subtle" size="xs" color="gray" className="text-zinc-400 hover:text-white hover:bg-zinc-800">
                    <ExternalLink size={14} />
                  </Button>
                </a>
                <Button
                  variant="subtle"
                  size="xs"
                  color="red"
                  leftSection={<LogOut size={14} />}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={() => {
                    logout();
                    navigate("/login", { replace: true });
                  }}
                >
                  Logout
                </Button>
              </Group>
            )}
          </div>
        </div>
      </AppShell.Header>

      <AppShell.Navbar className="sidebar-nav overflow-y-auto overflow-x-hidden">
        <Sidebar onClose={close} />
      </AppShell.Navbar>

      <AppShell.Main>
        <div className="layout-content">
          {children}
        </div>
      </AppShell.Main>
    </AppShell>
  );
};

export default Layouts;
